// Déplacement d'un match à l'extérieur (D-053) : RDV au gymnase, départ, arrivée 1 h avant
// le match, distance et temps de route depuis notre gymnase, lien vers l'itinéraire.
// La salle vient de la FFBB (géolocalisée) ; sinon le staff la choisit dans une liste
// d'adresses. Notre gymnase : choisi par le bureau, sinon notre salle à domicile habituelle.
import { clubLeads, matchTrip, saveTrip, setClubTrips, setMatchVenue } from '../lib/api';
import { route, searchPlaces } from '../lib/trip';
import { advance, cacheValid, duration, mapsUrl, schedule, type Place, type TripInfo } from '../domain/trip';
import { type Ctx } from './context';
import { $, $$, html, mount, openSheet, toast } from './dom';
import { time } from './format';

export async function renderTrip(el: HTMLElement, ctx: Ctx, eventId: string, clubId: string, staff: boolean, ffbb = false, onVenue?: () => void): Promise<void> {
  const info = await matchTrip(eventId).catch(() => null);
  if (!info?.away) { mount(el, html``); return; }
  const admin = ctx.adminOf.some(c => c.id === clubId);
  const reload = () => void renderTrip(el, ctx, eventId, clubId, staff, ffbb, onVenue);
  // Un match FFBB garde la salle de la FFBB (la synchro la remettrait) ; hors FFBB, le staff la choisit.
  const pickVenue = staff && !ffbb;
  const head = html`<div class="block-head"><h3>Déplacement</h3>${admin ? html`<button type="button" class="btn btn-link btn-sm" id="tp-set">Réglages</button>` : ''}</div>`;
  const wire = () => {
    $(el, '#tp-set')?.addEventListener('click', () => void openTripSettings(clubId, info, reload));
    $(el, '#tp-venue')?.addEventListener('click', () => openPlacePicker('La salle du match', info.to?.label ?? '', async p => {
      try { await setMatchVenue(eventId, p); toast('Salle enregistrée'); if (onVenue) onVenue(); else reload(); } catch { toast('Impossible.', 'ko'); }
    }, info.from));
    $(el, '#tp-gym')?.addEventListener('click', () => openPlacePicker('Notre gymnase (départ des déplacements)', info.from?.label ?? '', async p => {
      try { await setClubTrips(clubId, { gym: p }); toast('Gymnase enregistré'); reload(); } catch { toast('Impossible.', 'ko'); }
    }));
  };

  if (!info.to || !info.from) {
    mount(el, html`<section class="block">${head}
      ${!info.to ? html`<p class="muted small">La salle du match n'est pas localisée${ffbb && staff ? ' : synchronise le calendrier FFBB (onglet Matchs).' : pickVenue ? ' : choisis-la.' : '.'}</p>
        ${pickVenue ? html`<button type="button" class="btn btn-ghost btn-sm" id="tp-venue">Choisir la salle</button>` : ''}` : ''}
      ${!info.from ? html`<p class="muted small">Le point de départ (notre gymnase) n'est pas connu${admin ? '.' : ' : le bureau peut l’indiquer.'}</p>
        ${admin ? html`<button type="button" class="btn btn-ghost btn-sm" id="tp-gym">Choisir notre gymnase</button>` : ''}` : ''}
    </section>`);
    wire();
    return;
  }

  const from = info.from, to = info.to;
  let leg: { km: number; min: number } | null = cacheValid(info) ? { km: info.trip.km, min: info.trip.min } : null;
  if (!leg) {
    mount(el, html`<section class="block">${head}<p class="muted small">Calcul du trajet…</p></section>`);
    leg = await route(from, to).catch(() => null);
    if (leg && staff) void saveTrip(eventId, { ...leg, from: [from.lat, from.lng], to: [to.lat, to.lng] }).catch(() => undefined);
  }
  const s = leg ? schedule(new Date(info.starts_at), leg.min, info.arrive_min, info.meet_min) : null;
  mount(el, html`
    <section class="block tp">
      ${head}
      ${s ? html`
        <div class="tp-meet">
          <span class="eyebrow">Rendez-vous</span>
          <b>${time(s.meet)}</b>
          <span>au ${from.label ?? 'gymnase'}</span>
        </div>
        <ol class="tp-steps">
          <li><span>${time(s.depart)}</span>Départ</li>
          <li><span>${time(s.arrive)}</span>Arrivée, ${advance(info.arrive_min)} avant le match</li>
          <li><span>${time(new Date(info.starts_at))}</span>Coup d'envoi</li>
        </ol>
        <p class="muted small">${String(leg!.km).replace('.', ',')} km · ${duration(leg!.min)} de route → ${to.label ?? 'salle du match'}</p>`
        : html`<p class="muted small">Itinéraire indisponible pour le moment : ${to.label ?? 'salle du match'}.</p>`}
      <div class="btn-row">
        <a class="btn btn-ghost btn-sm" href="${mapsUrl(from, to)}" target="_blank" rel="noopener">Itinéraire</a>
        ${pickVenue ? html`<button type="button" class="btn btn-link btn-sm" id="tp-venue">Changer de salle</button>` : ''}
      </div>
    </section>`);
  wire();
}

/** Choisir une adresse ou un lieu dans une liste (Géoplateforme IGN) : rien n'est deviné. */
export function openPlacePicker(title: string, initial: string, onPick: (p: Place) => void, near: Place | null = null) {
  openSheet(title, html`
    <label class="field"><span>Adresse, salle ou commune</span>
      <input id="pp-q" type="search" value="${initial}" placeholder="ex. 12 rue des Sports, Valbrenne" autocomplete="off"></label>
    <ul class="pick-list" id="pp-res"></ul>
    <p class="hint">Choisis la bonne ligne : le trajet part de là.</p>`, (sheet, close) => {
    const q = $<HTMLInputElement>(sheet, '#pp-q')!, res = $(sheet, '#pp-res')!;
    let timer = 0, seq = 0;
    const search = async () => {
      const n = ++seq;
      const list = await searchPlaces(q.value, near).catch(() => null);
      if (n !== seq) return;
      mount(res, list === null ? html`<li class="muted small">Recherche indisponible.</li>`
        : list.length ? html`${list.map((p, i) => html`<li><button type="button" data-p="${i}"><span class="pick-main"><strong>${p.label ?? '—'}</strong></span><span class="chip chip-ok">Choisir</span></button></li>`)}`
        : html`<li class="muted small">${q.value.trim().length < 3 ? 'Tape au moins 3 lettres.' : 'Rien trouvé.'}</li>`);
      for (const b of $$<HTMLButtonElement>(res, '[data-p]')) b.addEventListener('click', () => { close(); onPick(list![Number(b.dataset.p)]!); });
    };
    q.addEventListener('input', () => { clearTimeout(timer); timer = window.setTimeout(() => void search(), 350); });
    if (q.value.trim()) void search();
    q.focus();
  });
}

/** Réglages du club : notre gymnase, l'avance à l'arrivée, le rendez-vous avant le départ, et celui d'un match à
 *  domicile (repère de « sans échauffement » pour une indisponibilité avec une heure, D-114). */
async function openTripSettings(clubId: string, info: TripInfo, onDone: () => void) {
  const leads = await clubLeads(clubId);
  const opt = (vals: number[], cur: number) => vals.map(v => html`<option value="${v}" ${v === cur ? 'selected' : ''}>${v ? advance(v) : 'à l’heure du départ'}</option>`);
  openSheet('Réglages des déplacements', html`
    <form class="form" id="f-tp" novalidate>
      <div class="field"><span>Départ des déplacements</span>
        <button type="button" class="btn btn-ghost" id="tp-gym2">${info.from?.label ?? 'Choisir notre gymnase'}</button></div>
      <label class="field"><span>Arriver avant le coup d'envoi</span>
        <select name="arrive">${opt([30, 45, 60, 75, 90, 120], info.arrive_min)}</select></label>
      <label class="field"><span>Rendez-vous avant le départ</span>
        <select name="meet">${opt([0, 10, 15, 20, 30], info.meet_min)}</select></label>
      <label class="field"><span>Rendez-vous avant un match à domicile</span>
        <select name="home">${opt([15, 30, 45, 60, 75, 90], leads.home)}</select>
        <small class="muted">Arrivée après ce rendez-vous (une indisponibilité avec une heure) : « sans échauffement ».</small></label>
      <button class="btn btn-primary" type="submit">Enregistrer</button>
    </form>`, (sheet, close) => {
    $(sheet, '#tp-gym2')!.addEventListener('click', () => { close(); openPlacePicker('Notre gymnase (départ des déplacements)', info.from?.label ?? '', async p => {
      try { await setClubTrips(clubId, { gym: p }); toast('Gymnase enregistré'); onDone(); } catch { toast('Impossible.', 'ko'); }
    }); });
    const f = $<HTMLFormElement>(sheet, '#f-tp')!;
    f.addEventListener('submit', async e => {
      e.preventDefault();
      const d = new FormData(f);
      try { await setClubTrips(clubId, { arrive: Number(d.get('arrive')), meet: Number(d.get('meet')), home: Number(d.get('home')) }); close(); toast('Réglages enregistrés'); onDone(); }
      catch { toast('Impossible.', 'ko'); }
    });
  });
}
